import { Link } from 'react-router-dom';
import { Menu, X, CreditCard, Megaphone, MessageCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LanguageSelector from './LanguageSelector';
import SupportModal from '../Support/SupportModal';

const TopBar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { currentUser, userData, logout } = useAuth();
  const { t } = useLanguage();
  const [advertisingEnabled, setAdvertisingEnabled] = useState(true);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [hasUnreadSupportResponse, setHasUnreadSupportResponse] = useState(false);

  useEffect(() => {
    const checkAdvertisingStatus = async () => {
      try {
        const systemSettingsDoc = await getDoc(doc(db, 'systemParams', 'systemSettings'));
        if (systemSettingsDoc.exists()) {
          const settings = systemSettingsDoc.data();
          setAdvertisingEnabled(settings.advertisingEnabled !== false);
        }
      } catch (error) {
        console.warn('Could not fetch advertising status:', error);
        // Default to enabled if we can't fetch the setting
        setAdvertisingEnabled(true);
      }
    };

    checkAdvertisingStatus();
  }, []);

  // Listen for unread support responses
  useEffect(() => {
    if (!currentUser) {
      setHasUnreadSupportResponse(false);
      return;
    }

    // Use a simpler approach to check for unread responses
    const checkUnreadResponses = async () => {
      try {
        const supportRef = collection(db, 'supportMessages');
        const q = query(
          supportRef,
          where('userId', '==', currentUser.uid)
        );
        
        const snapshot = await getDocs(q);
        const hasUnread = snapshot.docs.some(doc => {
          const data = doc.data();
          return data.answered === true && data.userNotified === false;
        });
        
        setHasUnreadSupportResponse(hasUnread);
      } catch (error) {
        console.warn('Error checking for support responses:', error);
        setHasUnreadSupportResponse(false);
      }
    };

    // Check immediately and then every 30 seconds
    checkUnreadResponses();
    const interval = setInterval(checkUnreadResponses, 30000);
    return () => clearInterval(interval);
  }, [currentUser]);

  const toggleMenu = () => {
    setMenuOpen(prev => !prev);
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo and App Name */}
        <Link to="/" className="flex items-center">
          <img src="/Logo_OpportunAi-01.png" alt="OpportunAI" style={{ height: '2.42rem' }} className="w-auto" />
        </Link>

        {/* Language Selector and Menu */}
        <div className="flex items-center space-x-4">
          <LanguageSelector />
          
          {/* Support Button - Only show for authenticated users */}
          {currentUser && (userData?.role === 'requester' || userData?.role === 'provider') && (
            <div className="relative">
              <button
                onClick={() => setShowSupportModal(true)}
                className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
                aria-label="Contact Support"
              >
                <div className="relative">
                  <MessageCircle className={`h-5 w-5 ${hasUnreadSupportResponse ? 'text-primary-600' : 'text-gray-600'}`} />
                  {hasUnreadSupportResponse && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse">
                      <div className="absolute inset-0 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                    </div>
                  )}
                </div>
                <span className={`text-sm font-medium hidden sm:inline ${hasUnreadSupportResponse ? 'text-primary-600' : 'text-gray-700'}`}>
                  Support
                </span>
              </button>
            </div>
          )}
          
          <button 
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            onClick={toggleMenu}
            aria-label="Menu"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="absolute top-full left-0 right-0 bg-white shadow-lg z-40 border-t border-gray-200">
          <div className="container mx-auto px-4 py-3">
            <ul className="space-y-3">
              {!currentUser ? (
                <>
                  <li>
                    <Link 
                      to="/login" 
                      className="block py-2 px-4 hover:bg-primary-50 rounded transition-colors"
                      onClick={toggleMenu}
                    >
                      {t('nav.login')}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/register" 
                      className="block py-2 px-4 hover:bg-primary-50 rounded transition-colors"
                      onClick={toggleMenu}
                    >
                      {t('nav.register')}
                    </Link>
                  </li>
                </>
              ) : (
                <>
                  <li className="px-4 py-2 text-sm text-gray-500">
                    Signed in as {userData?.username || userData?.email}
                  </li>
                  
                  <li>
                    <Link 
                      to="/business-plan" 
                      className="block py-2 px-4 hover:bg-primary-50 rounded transition-colors flex items-center"
                      onClick={toggleMenu}
                    >
                      <CreditCard className="h-5 w-5 mr-2" />
                      {t('nav.businessPlan')}
                    </Link>
                  </li>

                  {userData?.role === 'provider' && advertisingEnabled && (
                    <li>
                      <Link 
                        to="/advertising" 
                        className="block py-2 px-4 hover:bg-primary-50 rounded transition-colors flex items-center"
                        onClick={toggleMenu}
                      >
                        <Megaphone className="h-5 w-5 mr-2" />
                        {t('advertising.title')}
                      </Link>
                    </li>
                  )}
                  
                  {userData?.role === 'requester' && (
                    <>
                      <li>
                        <Link 
                          to="/requester/profile" 
                          className="block py-2 px-4 hover:bg-primary-50 rounded transition-colors"
                          onClick={toggleMenu}
                        >
                          {t('nav.profile')}
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/requester/dashboard" 
                          className="block py-2 px-4 hover:bg-primary-50 rounded transition-colors"
                          onClick={toggleMenu}
                        >
                          {t('nav.dashboard')}
                        </Link>
                      </li>
                    </>
                  )}

                  {userData?.role === 'provider' && (
                    <>
                      <li>
                        <Link 
                          to="/provider/profile" 
                          className="block py-2 px-4 hover:bg-primary-50 rounded transition-colors"
                          onClick={toggleMenu}
                        >
                          {t('nav.profile')}
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/provider/dashboard" 
                          className="block py-2 px-4 hover:bg-primary-50 rounded transition-colors"
                          onClick={toggleMenu}
                        >
                          {t('nav.dashboard')}
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/marketplace" 
                          className="block py-2 px-4 hover:bg-primary-50 rounded transition-colors"
                          onClick={toggleMenu}
                        >
                          {t('nav.marketplace')}
                        </Link>
                      </li>
                    </>
                  )}

                  {userData?.role === 'admin' && (
                    <li>
                      <Link 
                        to="/admin/dashboard" 
                        className="block py-2 px-4 hover:bg-primary-50 rounded transition-colors"
                        onClick={toggleMenu}
                      >
                        {t('nav.adminDashboard')}
                      </Link>
                    </li>
                  )}

                  <li>
                    <button 
                      onClick={() => {
                        logout();
                        toggleMenu();
                      }}
                      className="block w-full text-left py-2 px-4 hover:bg-primary-50 rounded transition-colors"
                    >
                      {t('nav.logout')}
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      )}
      
      {/* Support Modal */}
      <SupportModal 
        isOpen={showSupportModal} 
        onClose={() => setShowSupportModal(false)} 
      />
    </header>
  );
};

export default TopBar;